const API_BASE_URL = (
    process.env.REACT_APP_DIRECT_BACKEND_URL
    || process.env.REACT_APP_API_BASE_URL
    || 'http://127.0.0.1:8000'
).replace(/\/$/, '');
export const getDjangoAdminUrl = () => `${API_BASE_URL}/admin/`;
const TOKEN_STORAGE_KEY = 'examsite.auth';
const MUTATION_COOLDOWN_MS = 1000;
const inFlightMutations = new Map();
const recentMutations = new Map();
let tokenRefreshPromise = null;

const decodeJwtPayload = (token) => {
    if (typeof token !== 'string' || token.split('.').length !== 3) return null;
    try {
        const encodedPayload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const paddedPayload = encodedPayload.padEnd(Math.ceil(encodedPayload.length / 4) * 4, '=');
        return JSON.parse(decodeURIComponent(
            atob(paddedPayload)
                .split('')
                .map((character) => `%${character.charCodeAt(0).toString(16).padStart(2, '0')}`)
                .join(''),
        ));
    } catch {
        return null;
    }
};

const isAccessTokenValid = (token) => {
    if (!token) return false;
    const payload = decodeJwtPayload(token);
    if (!payload) return token.split('.').length !== 3;
    return typeof payload.exp !== 'number' || payload.exp * 1000 > Date.now();
};

export const getAccessCapabilities = (user = {}) => {
    // Access capabilities are authoritative backend facts. Never infer a role
    // from a group name: course-role groups are linked to Course records and a
    // similarly named arbitrary group must not grant UI access.
    const isPlatformAdmin = Boolean(user.isPlatformAdmin ?? user.is_platform_admin);
    const isOrganizationManager = Boolean(user.is_organization_master);
    const canTeach = Boolean(
        isPlatformAdmin ||
        isOrganizationManager ||
        user.is_teacher
    );
    const canStudy = Boolean(user.is_student);

    return {
        isPlatformAdmin,
        isOrganizationManager,
        canTeach,
        canStudy,
        availableRoles: [canTeach ? 'teacher' : null, canStudy ? 'student' : null].filter(Boolean),
    };
};

export const normalizeRole = (user = {}) => {
    const {canTeach, canStudy} = getAccessCapabilities(user);
    if (canTeach) return 'teacher';
    if (canStudy) return 'student';

    return null;
};

const parseResponse = async (response) => {
    if (response.status === 204) return null;
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) return response.text();
    try { return await response.json(); }
    catch { return null; }
};

const SERVER_ERROR_MESSAGE = '\u062E\u0637\u0627\u06CC \u062F\u0627\u062E\u0644\u06CC \u0633\u0631\u0648\u0631 \u0631\u062E \u062F\u0627\u062F. \u0644\u0637\u0641\u0627\u064B \u06A9\u0645\u06CC \u0628\u0639\u062F \u062F\u0648\u0628\u0627\u0631\u0647 \u062A\u0644\u0627\u0634 \u06A9\u0646\u06CC\u062F.';
const BAD_GATEWAY_ERROR_MESSAGE = '\u0633\u0631\u0648\u0631 \u0645\u0648\u0642\u062A\u0627\u064B \u062F\u0631 \u062F\u0633\u062A\u0631\u0633 \u0646\u06CC\u0633\u062A. \u0644\u0637\u0641\u0627\u064B \u06A9\u0645\u06CC \u0628\u0639\u062F \u062F\u0648\u0628\u0627\u0631\u0647 \u062A\u0644\u0627\u0634 \u06A9\u0646\u06CC\u062F.';
const INVALID_RESPONSE_MESSAGE = '\u067E\u0627\u0633\u062E \u0646\u0627\u0645\u0639\u062A\u0628\u0631\u06CC \u0627\u0632 \u0633\u0631\u0648\u0631 \u062F\u0631\u06CC\u0627\u0641\u062A \u0634\u062F.';

const looksLikeHtml = (value) => (
    typeof value === 'string' &&
    /<!doctype\s+html|<html[\s>]|<head[\s>]|<body[\s>]|<\/[a-z][\s\S]*>/i.test(value)
);

const getStatusErrorMessage = (status) => {
    if (status === 500) return SERVER_ERROR_MESSAGE;
    if ([502, 503, 504].includes(status)) return BAD_GATEWAY_ERROR_MESSAGE;
    if (status >= 500) return SERVER_ERROR_MESSAGE;
    return '';
};

const ERROR_FALLBACKS = {
    400: 'اطلاعات ارسال‌شده نامعتبر است.',
    401: 'برای ادامه وارد حساب کاربری شوید.',
    403: 'شما اجازه انجام این عملیات را ندارید.',
    404: 'مورد درخواستی یافت نشد.',
    409: 'اطلاعات تغییر کرده است؛ دوباره تلاش کنید.',
    413: 'حجم فایل یا درخواست بیش از حد مجاز است.',
    415: 'نوع فایل یا محتوای درخواست پشتیبانی نمی‌شود.',
    429: 'تعداد درخواست‌ها بیش از حد مجاز است.',
    500: SERVER_ERROR_MESSAGE,
    501: SERVER_ERROR_MESSAGE,
    502: BAD_GATEWAY_ERROR_MESSAGE,
    503: BAD_GATEWAY_ERROR_MESSAGE,
    504: BAD_GATEWAY_ERROR_MESSAGE,
};

const getSafeErrorText = (value, status) => {
    if (typeof value !== 'string') return '';
    const message = value.replace(/\s+/g, ' ').trim();
    if (!message) return '';
    if (looksLikeHtml(message)) return getStatusErrorMessage(status) || INVALID_RESPONSE_MESSAGE;
    return message;
};

const collectErrorStrings = (value, status) => {
    if (typeof value === 'string') {
        const message = getSafeErrorText(value, status);
        return message ? [message] : [];
    }
    if (Array.isArray(value)) return value.flatMap((item) => collectErrorStrings(item, status));
    if (value && typeof value === 'object') {
        return Object.values(value).flatMap((item) => collectErrorStrings(item, status));
    }
    return [];
};

export const normalizeApiError = (status = 0, body) => {
    const data = body && typeof body === 'object' && !Array.isArray(body) ? body : {};
    const nestedError = data.error && typeof data.error === 'object' && !Array.isArray(data.error)
        ? data.error
        : {};
    const fields = {};
    const explicitFields = data.field_errors;

    if (explicitFields && typeof explicitFields === 'object' && !Array.isArray(explicitFields)) {
        Object.entries(explicitFields).forEach(([field, value]) => {
            const messages = collectErrorStrings(value, status);
            if (messages.length) fields[field] = messages;
        });
    } else if (!collectErrorStrings(data.detail, status).length && !collectErrorStrings(data.message, status).length) {
        const reserved = new Set(['code', 'detail', 'message', 'error', 'field_errors']);
        Object.entries(data).forEach(([field, value]) => {
            if (reserved.has(field)) return;
            const messages = collectErrorStrings(value, status);
            if (messages.length) fields[field] = messages;
        });
    }

    const message = collectErrorStrings(data.detail, status)[0]
        || collectErrorStrings(data.message, status)[0]
        || collectErrorStrings(nestedError.message, status)[0]
        || Object.values(fields).flat()[0]
        || ERROR_FALLBACKS[status]
        || (status >= 500 ? SERVER_ERROR_MESSAGE : 'انجام درخواست ناموفق بود.');
    const code = typeof data.code === 'string'
        ? data.code
        : typeof nestedError.code === 'string' ? nestedError.code : undefined;

    return {status, code, message, fields};
};

const translatedBackendMessages = {
    "Resource is outside the teacher's scope.": 'این منبع در محدودهٔ دسترسی شما نیست. منبع دیگری انتخاب کنید.',
    "Learner is outside the teacher's scope.": 'این دانش‌آموز در محدودهٔ دسترسی شما نیست. دانش‌آموز دیگری انتخاب کنید.',
    'This exam is not open now.': 'این آزمون در حال حاضر قابل شروع نیست.',
    'Exam time is finished. You cannot start a submission now.': 'زمان آزمون پایان یافته و تلاش فعالی برای نهایی‌سازی پیدا نشد.',
    'Exam time has expired. Saved answers were finalized automatically.': 'زمان آزمون پایان یافت و پاسخ‌های ذخیره‌شده به‌صورت خودکار نهایی شدند.',
    'Exam time expired while submitting. Received answers were finalized within the network grace period.': 'زمان آزمون هنگام ارسال پایان یافت؛ پاسخ‌ها در مهلت کوتاه بازیابی شبکه دریافت و نهایی شدند.',
    'This exam attempt was already finalized.': 'این تلاش قبلاً نهایی شده است.',
    'This exam is not open for joining.': 'در حال حاضر امکان پیوستن به این آزمون وجود ندارد.',
    'Maximum number of attempts has been reached.': 'حداکثر تعداد تلاش‌های مجاز این آزمون انجام شده است.',
    'An in-progress attempt cannot be graded.': 'تا پیش از ارسال پاسخ‌ها امکان تصحیح این تلاش وجود ندارد.',
    'Automatically graded answers are read-only.': 'پاسخ‌های دارای تصحیح خودکار فقط خواندنی هستند.',
    'One or more answers do not belong to this attempt.': 'یک یا چند پاسخ متعلق به این تلاش نیست.',
    'Each answer can only appear once.': 'هر پاسخ فقط یک‌بار قابل ثبت است.',
    'Awarded points cannot be negative.': 'نمره نمی‌تواند منفی باشد.',
    'Awarded points cannot exceed question points.': 'نمره نمی‌تواند بیشتر از بارم سؤال باشد.',
    'Total score must be greater than zero.': 'نمره کل آزمون باید بیشتر از صفر باشد.',
    'The total score is too small for the number of questions.': 'نمره کل برای تعداد سؤال‌های انتخاب‌شده بیش از حد کوچک است.',
    'Only published exams can be returned to draft.': 'فقط آزمون منتشرشده را می‌توان به پیش‌نویس بازگرداند.',
    'This exam cannot be returned to draft because one or more attempts have started.': 'این آزمون قابل بازگشت به پیش‌نویس نیست، چون حداقل یک دانشجو آن را شروع کرده است.',
    'A draft exam has no results to release.': 'آزمون پیش‌نویس نتیجه‌ای برای انتشار ندارد.',
    'A draft exam cannot release results.': 'آزمون پیش‌نویس نتیجه‌ای برای انتشار ندارد.',
    'Results cannot be released before the exam ends.': 'نتایج فقط پس از پایان آزمون قابل انتشار هستند.',
    'Results cannot be released while attempts are in progress.': 'تا وقتی تلاش فعالی وجود دارد، نتایج قابل انتشار نیستند.',
    'All attempts must be finished before results are released.': 'تا وقتی تلاش فعالی وجود دارد، نتایج قابل انتشار نیستند.',
    'All submitted answers must be graded before results are released.': 'پیش از انتشار نتایج باید تمام پاسخ‌های ارسال‌شده تصحیح شوند.',
};

const engineUsageMessages = {
    request_units: (data) => `حداکثر تعداد مجاز برای هر درخواست ${data?.limit ?? ''} سؤال است.`.replace('  ', ' '),
    daily_user_units: () => 'سهمیهٔ امروز شما تکمیل شده است.',
    monthly_user_units: () => 'سهمیهٔ این ماه شما تکمیل شده است.',
    concurrent_user: () => 'ابتدا منتظر پایان یکی از پردازش‌های فعال بمانید.',
    daily_organization_units: () => 'سهمیهٔ روزانهٔ سازمان تکمیل شده است.',
    monthly_organization_units: () => 'سهمیهٔ ماهانهٔ سازمان تکمیل شده است.',
    concurrent_organization: () => 'ظرفیت پردازش هم‌زمان سازمان تکمیل است.',
    role_not_allowed: () => 'این قابلیت فقط برای معلم و مدیر سازمان فعال است.',
};

export const getEngineUsageErrorMessage = (error) => {
    const code = error?.code || error?.data?.code || error?.data?.error;
    return engineUsageMessages[code]?.(error?.data) || '';
};

const translateBackendMessage = (message) => translatedBackendMessages[message] || message;

export const getErrorMessage = (error) => {
    if (error?.code === 'DUPLICATE_REQUEST') return 'این درخواست قبلاً ارسال شده است؛ لطفاً منتظر نتیجه بمانید.';
    const usageMessage = getEngineUsageErrorMessage(error);
    if (usageMessage) return usageMessage;
    if (!error?.status && !error?.data) {
        return translateBackendMessage(getSafeErrorText(error?.message, 0) || 'ارتباط با سرور برقرار نشد.');
    }
    const normalized = error?.normalized || normalizeApiError(error?.status, error?.data);
    const retrySuffix = error?.status === 429 && error?.retryAfter
        ? ` ${error.retryAfter} ثانیه دیگر دوباره تلاش کنید.`
        : '';
    return `${translateBackendMessage(normalized.message)}${retrySuffix}`;
};

export const getErrorFields = (error) => (
    error?.normalized?.fields || normalizeApiError(error?.status, error?.data).fields
);

const createApiError = (response, data) => {
    const normalized = normalizeApiError(response.status, data);
    const error = new Error(normalized.message);
    error.status = response.status;
    error.data = data;
    error.code = normalized.code;
    error.fields = normalized.fields;
    error.normalized = normalized;
    if (response.status === 429) error.retryAfter = getRetryAfterSeconds(response);
    return error;
};

export const authStorage = {
    get: () => {
        try {
            return JSON.parse(localStorage.getItem(TOKEN_STORAGE_KEY)) || {};
        } catch {
            return {};
        }
    },
    save: ({access, refresh, user}) => {
        const previous = authStorage.get();
        const sessionUser = user || previous.user;
        const capabilities = getAccessCapabilities({
            ...(decodeJwtPayload(access || previous.access) || {}),
            ...(sessionUser || {}),
        });
        const preferredRole = previous.activeRole;
        const activeRole = capabilities.availableRoles.includes(preferredRole)
            ? preferredRole
            : capabilities.canTeach ? 'teacher' : capabilities.canStudy ? 'student' : null;
        const session = {
            access: access || previous.access,
            refresh: refresh || previous.refresh,
            user: sessionUser,
            activeRole,
        };
        localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(session));
        window.dispatchEvent(new Event('auth-change'));
        return session;
    },
    clear: () => {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        window.dispatchEvent(new Event('auth-change'));
    },
    isAuthenticated: () => isAccessTokenValid(authStorage.get().access),
    isPlatformAdmin: () => {
        const session = authStorage.get();
        return getAccessCapabilities({
            ...(decodeJwtPayload(session.access) || {}),
            ...(session.user || {}),
        }).isPlatformAdmin;
    },
    getCapabilities: () => {
        const session = authStorage.get();
        return getAccessCapabilities({
            ...(decodeJwtPayload(session.access) || {}),
            ...(session.user || {}),
        });
    },
    setActiveRole: (role) => {
        const session = authStorage.get();
        const capabilities = authStorage.getCapabilities();
        if (!capabilities.availableRoles.includes(role)) return false;
        localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify({...session, activeRole: role}));
        window.dispatchEvent(new Event('auth-change'));
        return true;
    },
    getRole: () => {
        const session = authStorage.get();
        const capabilities = authStorage.getCapabilities();
        if (capabilities.availableRoles.includes(session.activeRole)) return session.activeRole;
        return capabilities.canTeach ? 'teacher' : capabilities.canStudy ? 'student' : null;
    },
};

const getRetryAfterSeconds = (response) => {
    const value = response.headers.get('retry-after');
    if (!value) return null;
    const seconds = Number(value);
    if (Number.isFinite(seconds)) return Math.max(1, Math.ceil(seconds));
    const retryDate = Date.parse(value);
    return Number.isNaN(retryDate) ? null : Math.max(1, Math.ceil((retryDate - Date.now()) / 1000));
};

const performRequest = async (path, options = {}, allowRefresh = true) => {
    const headers = {...options.headers};
    if (options.auth) {
        const access = authStorage.get().access;
        if (access) headers.Authorization = `Bearer ${access}`;
    }
    if (options.body && !(options.body instanceof FormData)) headers['Content-Type'] = 'application/json';

    let response;
    try {
        response = await fetch(`${API_BASE_URL}${path}`, {...options, headers});
    } catch {
        throw new Error('ارتباط با سرور برقرار نشد. اجرای بک‌اند و تنظیمات CORS را بررسی کنید.');
    }

    if (response.status === 401 && options.auth && allowRefresh && authStorage.get().refresh) {
        try {
            await refreshAccessToken();
            return performRequest(path, options, false);
        } catch {
            authStorage.clear();
        }
    } else if (response.status === 401 && options.auth) {
        authStorage.clear();
    }

    const data = await parseResponse(response);
    if (!response.ok) {
        throw createApiError(response, data);
    }
    return data;
};

const refreshAccessToken = () => {
    if (tokenRefreshPromise) return tokenRefreshPromise;
    const refresh = authStorage.get().refresh;
    if (!refresh) return Promise.reject(new Error('Refresh token is unavailable'));

    tokenRefreshPromise = performRequest('/api/accounts/token/refresh/', {
        method: 'POST',
        body: JSON.stringify({refresh}),
    }, false)
        .then((tokens) => authStorage.save(tokens))
        .finally(() => { tokenRefreshPromise = null; });
    return tokenRefreshPromise;
};

export const ensureAuthenticated = async () => {
    if (authStorage.isAuthenticated()) return true;
    if (!authStorage.get().refresh) return false;
    try {
        await refreshAccessToken();
        return authStorage.isAuthenticated();
    } catch {
        authStorage.clear();
        return false;
    }
};

const request = (path, options = {}, allowRefresh = true) => {
    const method = (options.method || 'GET').toUpperCase();
    if (method === 'GET' || method === 'HEAD' || options.allowDuplicate) {
        return performRequest(path, options, allowRefresh);
    }

    const mutationKey = `${method}:${path}`;
    const lastRequestAt = recentMutations.get(mutationKey) || 0;
    if (inFlightMutations.has(mutationKey) || Date.now() - lastRequestAt < MUTATION_COOLDOWN_MS) {
        const error = new Error('Duplicate request blocked');
        error.code = 'DUPLICATE_REQUEST';
        return Promise.reject(error);
    }

    recentMutations.set(mutationKey, Date.now());
    const pendingRequest = performRequest(path, options, allowRefresh)
        .finally(() => inFlightMutations.delete(mutationKey));
    inFlightMutations.set(mutationKey, pendingRequest);
    return pendingRequest;
};

export const apiRequest = request;

const learningConversationPath = (conversationId, suffix = '') => (
    `/api/learning-agent/conversations/${conversationId ? `${encodeURIComponent(conversationId)}/` : ''}${suffix}`
);

const withTimeout = (callback, timeoutMs = 75000) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    return callback(controller.signal).finally(() => clearTimeout(timeoutId));
};

export const learningAgentApi = {
    mode: () => request(learningConversationPath(null, 'mode/'), {auth: true}),
    conversations: () => request(learningConversationPath(), {auth: true}),
    conversation: (conversationId) => request(learningConversationPath(conversationId), {auth: true}),
    createConversation: (payload = {}) => request(learningConversationPath(), {
        method: 'POST', auth: true, body: JSON.stringify(payload),
    }),
    removeConversation: (conversationId) => request(learningConversationPath(conversationId), {
        method: 'DELETE', auth: true,
    }),
    agentState: (conversationId) => request(learningConversationPath(conversationId, 'agent-state/'), {auth: true}),
    chat: (conversationId, message) => withTimeout((signal) => request(
        learningConversationPath(conversationId, 'chat/'),
        {method: 'POST', auth: true, body: JSON.stringify({message}), signal},
    )),
    invokeTool: (conversationId, toolName, args = {}) => request(
        learningConversationPath(conversationId, 'invoke-tool/'),
        {
            method: 'POST',
            auth: true,
            allowDuplicate: true,
            body: JSON.stringify({tool_name: toolName, arguments: args}),
        },
    ),
    confirmGoal: (conversationId, confirmationToken) => request(
        learningConversationPath(conversationId, 'confirm-goal/'),
        {
            method: 'POST',
            auth: true,
            body: JSON.stringify({confirmation_token: confirmationToken}),
        },
    ),
    turns: (conversationId) => request(learningConversationPath(conversationId, 'turns/'), {auth: true}),
    turn: (conversationId, turnId) => request(learningConversationPath(conversationId, `turns/${encodeURIComponent(turnId)}/`), {auth: true}),
    path: (conversationId) => request(learningConversationPath(conversationId, 'path/'), {auth: true}),
    pathAccept: (conversationId, revision) => request(learningConversationPath(conversationId, 'path/accept/'), {
        method: 'POST', auth: true, body: JSON.stringify({expected_path_revision: revision}),
    }),
    pathCancel: (conversationId, revision) => request(learningConversationPath(conversationId, 'path/cancel/'), {
        method: 'POST', auth: true, body: JSON.stringify({expected_path_revision: revision}),
    }),
    sendInteractiveMessage: (conversationId, payload) => request(learningConversationPath(conversationId, 'messages/'), {
        method: 'POST', auth: true, allowDuplicate: true, body: JSON.stringify(payload),
    }),
    interaction: (conversationId, payload) => request(learningConversationPath(conversationId, 'interactions/'), {
        method: 'POST', auth: true, allowDuplicate: true, body: JSON.stringify(payload),
    }),
    interactions: (conversationId, params = {}) => request(
        `${learningConversationPath(conversationId, 'interactions/')}${buildQueryString(params)}`, {auth: true},
    ),
};

export const adminLearnerStateApi = {
    overview: (learnerId, {offset = 0, limit = 20} = {}) => request(
        `/api/learning-agent/admin/learners/${encodeURIComponent(learnerId)}/state/${buildQueryString({offset, limit})}`,
        {auth: true},
    ),
    graph: (learnerId, graphId, {
        conceptOffset = 0, conceptLimit = 100, chunkOffset = 0, chunkLimit = 100,
    } = {}) => request(
        `/api/learning-agent/admin/learners/${encodeURIComponent(learnerId)}/state/${buildQueryString({
            graph_id: graphId, concept_offset: conceptOffset, concept_limit: conceptLimit,
            chunk_offset: chunkOffset, chunk_limit: chunkLimit,
        })}`,
        {auth: true},
    ),
    resource: (learnerId, resourceId) => request(
        `/api/learning-agent/admin/learners/${encodeURIComponent(learnerId)}/state/${buildQueryString({resource_id: resourceId})}`,
        {auth: true},
    ),
    history: (learnerId, {graphId, from, to, limit = 100, cursor} = {}) => request(
        `/api/learning-agent/admin/learners/${encodeURIComponent(learnerId)}/state/history/${buildQueryString({
            graph_id: graphId, from, to, limit, cursor,
        })}`,
        {auth: true},
    ),
    courseSummary: (courseId, {q, offset = 0, limit = 25} = {}) => request(
        `/api/learning-agent/admin/courses/${encodeURIComponent(courseId)}/learners/state-summary/${buildQueryString({q, offset, limit})}`,
        {auth: true},
    ),
};

const parseFilename = (contentDisposition = '') => {
    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1].replace(/["']/g, ''));
    const plainMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
    return plainMatch?.[1] || null;
};

const requestBlob = async (path, options = {}, allowRefresh = true) => {
    const headers = {...options.headers};
    if (options.auth) {
        const access = authStorage.get().access;
        if (access) headers.Authorization = `Bearer ${access}`;
    }

    let response;
    try {
        response = await fetch(`${API_BASE_URL}${path}`, {...options, headers});
    } catch {
        throw new Error('ارتباط با سرور برقرار نشد. اجرای بک‌اند و تنظیمات CORS را بررسی کنید.');
    }

    if (response.status === 401 && options.auth && allowRefresh && authStorage.get().refresh) {
        try {
            await refreshAccessToken();
            return requestBlob(path, options, false);
        } catch {
            authStorage.clear();
        }
    } else if (response.status === 401 && options.auth) {
        authStorage.clear();
    }

    if (!response.ok) {
        const data = await parseResponse(response);
        throw createApiError(response, data);
    }

    return {
        blob: await response.blob(),
        filename: parseFilename(response.headers.get('content-disposition') || ''),
    };
};

const getCookieValue = (name) => {
    if (typeof document === 'undefined') return '';
    return document.cookie
        .split(';')
        .map((item) => item.trim())
        .find((item) => item.startsWith(`${name}=`))
        ?.slice(name.length + 1) || '';
};

const requestSession = (path, options = {}) => {
    const method = (options.method || 'GET').toUpperCase();
    const headers = {...options.headers};
    const csrfToken = getCookieValue('csrftoken');

    if (csrfToken && !['GET', 'HEAD', 'OPTIONS'].includes(method)) {
        headers['X-CSRFToken'] = decodeURIComponent(csrfToken);
    }
    if (options.body && !(options.body instanceof FormData)) headers['Content-Type'] = 'application/json';

    return request(path, {
        ...options,
        method,
        headers,
        credentials: 'include',
    });
};

export const accountsApi = {
    register: async (payload) => authStorage.save(await request('/api/accounts/register/', {
        method: 'POST',
        credentials: 'include',
        body: JSON.stringify(payload),
    })),
    login: async (payload) => authStorage.save(await request('/api/accounts/login/', {
        method: 'POST',
        credentials: 'include',
        body: JSON.stringify(payload),
    })),
    requestPasswordReset: (email) => request('/api/accounts/password-reset/request/', {
        method: 'POST',
        body: JSON.stringify({email}),
    }),
    confirmPasswordReset: (payload) => request('/api/accounts/password-reset/confirm/', {
        method: 'POST',
        body: JSON.stringify(payload),
    }),
    verifyToken: (token) => request('/api/accounts/token/verify/', {method: 'POST', body: JSON.stringify({token})}),
    profile: async () => {
        const user = await request('/api/accounts/profile/', {auth: true});
        authStorage.save({user});
        return user;
    },
    students: (params = {}) => request(`/api/accounts/students/${buildQueryString(params)}`, {auth: true}),
    organizations: {
        list: (params = {}) => request(`/api/accounts/organizations/${buildQueryString(params)}`, {auth: true}),
        detail: (id) => request(`/api/accounts/organizations/${id}/`, {auth: true}),
        create: (payload) => request('/api/accounts/organizations/', {method: 'POST', auth: true, body: JSON.stringify(payload)}),
        update: (id, payload) => request(`/api/accounts/organizations/${id}/`, {method: 'PATCH', auth: true, body: JSON.stringify(payload)}),
        remove: (id) => request(`/api/accounts/organizations/${id}/`, {method: 'DELETE', auth: true}),
        members: (id) => request(`/api/accounts/organizations/${id}/members/`, {auth: true}),
        memberOptions: (id, params = {}) => request(
            `/api/accounts/organizations/${id}/member-options/${buildQueryString(params)}`,
            {auth: true},
        ),
        addMember: (id, payload) => request(`/api/accounts/organizations/${id}/members/`, {
            method: 'POST',
            auth: true,
            body: JSON.stringify(payload),
        }),
        removeMember: (id, payload) => request(`/api/accounts/organizations/${id}/remove-member/`, {
            method: 'POST',
            auth: true,
            body: JSON.stringify(payload),
        }),
    },
    courses: {
        list: (params = {}) => request(`/api/accounts/courses/${buildQueryString(params)}`, {auth: true}),
        detail: (id) => request(`/api/accounts/courses/${id}/`, {auth: true}),
        create: (payload) => request('/api/accounts/courses/', {method: 'POST', auth: true, body: JSON.stringify(payload)}),
        update: (id, payload) => request(`/api/accounts/courses/${id}/`, {method: 'PATCH', auth: true, body: JSON.stringify(payload)}),
        remove: (id) => request(`/api/accounts/courses/${id}/`, {method: 'DELETE', auth: true}),
        members: (id) => request(`/api/accounts/courses/${id}/members/`, {auth: true}),
        memberOptions: (id, params = {}) => request(
            `/api/accounts/courses/${id}/member-options/${buildQueryString(params)}`,
            {auth: true},
        ),
        addMember: (id, payload) => request(`/api/accounts/courses/${id}/members/`, {
            method: 'POST',
            auth: true,
            body: JSON.stringify(payload),
        }),
        bulkAddMembers: (id, payload) => request(`/api/accounts/courses/${id}/bulk-members/`, {
            method: 'POST',
            auth: true,
            body: JSON.stringify(payload),
        }),
        createStudent: (id, payload) => request(`/api/accounts/courses/${id}/create-student/`, {
            method: 'POST',
            auth: true,
            body: JSON.stringify(payload),
        }),
        removeMember: (id, payload) => request(`/api/accounts/courses/${id}/remove-member/`, {
            method: 'POST',
            auth: true,
            body: JSON.stringify(payload),
        }),
        importStudents: (id, file, commit = false) => {
            const body = new FormData();
            body.append('file', file);
            body.append('commit', String(commit));
            return request(`/api/accounts/courses/${id}/import-students/`, {
                method: 'POST', auth: true, body,
            });
        },
    },
    updateProfile: async (payload) => {
        const user = await request('/api/accounts/profile/', {method: 'PATCH', auth: true, body: JSON.stringify(payload)});
        authStorage.save({user});
        return user;
    },
    changePassword: async (payload) => {
        const result = await request('/api/accounts/change-password/', {
            method: 'POST',
            auth: true,
            credentials: 'include',
            body: JSON.stringify(payload),
        });
        authStorage.clear();
        return result;
    },
    logout: async () => {
        const refresh = authStorage.get().refresh;
        try {
            if (refresh) await request('/api/accounts/logout/', {
                method: 'POST',
                auth: true,
                credentials: 'include',
                body: JSON.stringify({refresh}),
            });
        } finally {
            try {
                await fetch(`${API_BASE_URL}/api/accounts/session-logout/`, {
                    method: 'POST',
                    credentials: 'include',
                });
            } finally {
                authStorage.clear();
            }
        }
    },
};

export const topicsApi = {
    list: (query = '') => request(`/api/topics/${query}`, {auth: true}),
    detail: (id) => request(`/api/topics/${id}/`, {auth: true}),
    create: (payload) => request('/api/topics/', {method: 'POST', auth: true, body: JSON.stringify(payload)}),
    update: (id, payload) => request(`/api/topics/${id}/`, {method: 'PATCH', auth: true, body: JSON.stringify(payload)}),
    remove: (id) => request(`/api/topics/${id}/`, {method: 'DELETE', auth: true}),
    archive: (id) => request(`/api/topics/${id}/archive/`, {method: 'POST', auth: true}),
    unarchive: (id) => request(`/api/topics/${id}/unarchive/`, {method: 'POST', auth: true}),
    addResource: (id, resourceId) => request(`/api/topics/${id}/add-resource/`, {
        method: 'POST',
        auth: true,
        body: JSON.stringify({resource_id: resourceId}),
    }),
    removeResource: (id, resourceId) => request(`/api/topics/${id}/remove-resource/`, {
        method: 'POST',
        auth: true,
        body: JSON.stringify({resource_id: resourceId}),
    }),
    resources: (id) => request(`/api/topics/${id}/resources/`, {auth: true}),
};

export const subjectInstructionsApi = {
    list: (params = {}) => request(`/api/resources/subject-instructions/${buildQueryString(params)}`, {auth: true}),
    detail: (subjectType) => request(`/api/resources/subject-instructions/${encodeURIComponent(subjectType)}/`, {auth: true}),
    create: (payload) => request('/api/resources/subject-instructions/', {method: 'POST', auth: true, body: JSON.stringify(payload)}),
    update: (subjectType, payload) => request(`/api/resources/subject-instructions/${encodeURIComponent(subjectType)}/`, {
        method: 'PATCH',
        auth: true,
        body: JSON.stringify(payload),
    }),
    remove: (subjectType) => request(`/api/resources/subject-instructions/${encodeURIComponent(subjectType)}/`, {
        method: 'DELETE',
        auth: true,
    }),
};

const buildQueryString = (params = {}) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') searchParams.set(key, value);
    });
    const query = searchParams.toString();
    return query ? `?${query}` : '';
};

const listAllQuestions = async (params = {}) => {
    const items = [];
    let page = 1;

    while (true) {
        const response = await request(`/api/questions/${buildQueryString({...params, page, page_size: 100})}`, {auth: true});
        if (Array.isArray(response)) return response;

        const pageItems = Array.isArray(response?.results) ? response.results : [];
        items.push(...pageItems);
        if (!response?.next || pageItems.length === 0) return items;
        page += 1;
    }
};

export const resourcesApi = {
    list: () => request('/api/resources/', {auth: true}),
    listAll: async () => {
        const items = [];
        let path = '/api/resources/';
        const seen = new Set();
        while (path && !seen.has(path)) {
            seen.add(path);
            const response = await request(path, {auth: true});
            if (Array.isArray(response)) return [...items, ...response];
            items.push(...(response?.results || response?.items || []));
            const nextUrl = response?.next ? new URL(response.next, `${API_BASE_URL}/api/resources/`) : null;
            path = nextUrl ? `${nextUrl.pathname}${nextUrl.search}` : '';
        }
        return items;
    },
    detail: (id) => request(`/api/resources/${encodeURIComponent(id)}/`, {auth: true}),
    file: (id) => requestBlob(`/api/resources/${encodeURIComponent(id)}/file/`, {auth: true}),
    update: (id, payload) => request(`/api/resources/${encodeURIComponent(id)}/`, {
        method: 'PATCH',
        auth: true,
        body: JSON.stringify(payload),
    }),
    upload: (files, options = {}) => {
        const body = new FormData();
        Array.from(files).forEach((file) => body.append('files', file));
        if (options.subject_type) body.append('subject_type', options.subject_type);
        if (options.description) body.append('description', options.description);
        if (options.course_id) body.append('course_id', String(options.course_id));
        return request('/api/resources/upload/', {method: 'POST', auth: true, body});
    },
    remove: (id) => request(`/api/resources/${encodeURIComponent(id)}/`, {method: 'DELETE', auth: true}),
    ingest: (id, payload = {}) => request(`/api/resources/${encodeURIComponent(id)}/ingest/`, {
        method: 'POST',
        auth: true,
        body: JSON.stringify(payload),
    }),
    bulkIngest: (payload = {}) => request('/api/resources/ingest/', {
        method: 'POST',
        auth: true,
        body: JSON.stringify(payload),
    }),
    llmLogs: (params = {}) => request(`/api/resources/llm-logs/${buildQueryString(params)}`, {auth: true}),
    llmLogDetail: (id) => request(`/api/resources/llm-logs/${id}/`, {auth: true}),
};

export const questionsApi = {
    usage: (params = {}) => request(`/api/questions/usage/${buildQueryString(params)}`, {auth: true}),
    list: (params = {}) => request(`/api/questions/${buildQueryString(params)}`, {auth: true}),
    listAll: listAllQuestions,
    detail: (id) => request(`/api/questions/${id}/`, {auth: true}),
    create: (payload) => request('/api/questions/', {method: 'POST', auth: true, body: JSON.stringify(payload)}),
    update: (id, payload) => request(`/api/questions/${id}/`, {method: 'PATCH', auth: true, body: JSON.stringify(payload)}),
    remove: (id) => request(`/api/questions/${id}/`, {method: 'DELETE', auth: true}),
    importExcel: (file) => {
        const body = new FormData();
        body.append('file', file);
        return request('/api/questions/import-excel/', {method: 'POST', auth: true, body});
    },
    generate: (payload = {}) => {
        const hasScope = ['keywords', 'topics', 'slots'].some((key) => {
            const value = payload[key];
            if (Array.isArray(value)) return value.length > 0;
            if (value && typeof value === 'object') return Object.keys(value).length > 0;
            return typeof value === 'string' ? value.trim().length > 0 : Boolean(value);
        });
        if (!hasScope) {
            const error = new Error('برای تولید سؤال حداقل یکی از keywords، topics یا slots الزامی است.');
            error.code = 'QUESTION_SCOPE_REQUIRED';
            return Promise.reject(error);
        }
        return request('/api/questions/generate/', {
            method: 'POST',
            auth: true,
            body: JSON.stringify(payload),
        });
    },
    exportPdf: (params = {}) => requestBlob(`/api/questions/export-pdf/${buildQueryString(params)}`, {auth: true}),
    reportPdf: (params) => requestBlob(`/api/questions/report-pdf/${buildQueryString(params)}`, {auth: true}),
    reportPackage: (params) => requestBlob(`/api/questions/report-package/${buildQueryString(params)}`, {auth: true}),
};

export const tasksApi = {
    detail: (id) => request(`/api/tasks/${encodeURIComponent(id)}/`, {auth: true}),
};

// Educational Knowledge Graph endpoints intentionally use the Django session/CSRF
// flow. The module does not accept the application's JWT and no KG API key is ever
// exposed to the browser bundle. Deployments that enable KG_API_KEY should proxy
// these paths through a server-side BFF.
const kgResourcePath = (resourceId, suffix = '') => (
    `/kg/api/resources/${resourceId ? `${encodeURIComponent(resourceId)}/` : ''}${suffix}`
);

export const knowledgeGraphApi = {
    educationalGraph: (resourceId) => requestSession(`/kg/api/resources/${encodeURIComponent(resourceId)}/educational-graph/`),
    health: () => requestSession('/kg/api/health/'),
    supportedFormats: () => requestSession('/kg/api/supported-formats/'),
    resources: (params = {}) => requestSession(`/kg/api/resources/${buildQueryString(params)}`),
    resource: (resourceId) => requestSession(kgResourcePath(resourceId)),
    createResource: (payload) => requestSession(kgResourcePath(), {
        method: 'POST',
        body: JSON.stringify(payload),
    }),
    uploadResource: (file, fields = {}) => {
        const body = new FormData();
        body.append('file', file);
        Object.entries(fields).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                body.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
            }
        });
        return requestSession(kgResourcePath(), {method: 'POST', body});
    },
    updateResource: (resourceId, payload) => requestSession(kgResourcePath(resourceId), {
        method: 'PUT',
        body: JSON.stringify(payload),
    }),
    removeResource: (resourceId, async = false) => requestSession(
        `${kgResourcePath(resourceId)}${async ? '?async=1' : ''}`,
        {method: 'DELETE'},
    ),
    graph: (resourceId, includeText = false) => requestSession(
        `${kgResourcePath(resourceId, 'graph/')}${includeText ? '?include_text=1' : ''}`,
    ),
    related: (resourceId) => requestSession(kgResourcePath(resourceId, 'related/')),
    concepts: (params = {}) => requestSession(`/kg/api/concepts/${buildQueryString(params)}`),
    concept: async (conceptId) => {
        const concept = await requestSession(`/kg/api/concepts/${encodeURIComponent(conceptId)}/`);
        return {
            ...concept,
            resources: (concept.resources || []).map((resource) => ({
                ...resource,
                resourceTitle: resource.resource__title,
            })),
        };
    },
    conceptEvidence: (conceptId) => requestSession(
        `/kg/api/concepts/${encodeURIComponent(conceptId)}/evidence/`,
    ),
    run: (runId) => requestSession(`/kg/api/runs/${encodeURIComponent(runId)}/`),
};

export const questionEngineApi = {
    sessions: () => requestSession('/api/question-engine/api/sessions/'),
    createSession: ({document, resourceId, title, preset = 'balanced', interface_language = 'fa', auto_answer_clarifications = false}) => {
        const body = new FormData();
        if (document) body.append('document', document);
        if (resourceId) body.append('resource_id', resourceId);
        if (title) body.append('title', title);
        if (preset) body.append('assessment_preset', preset);
        if (interface_language) body.append('interface_language', interface_language);
        if (auto_answer_clarifications) body.append('auto_answer_clarifications', 'true');
        return requestSession('/api/question-engine/api/sessions/', {
            method: 'POST',
            body,
            allowDuplicate: true,
        });
    },
    status: (sessionId) => requestSession(`/api/question-engine/${encodeURIComponent(sessionId)}/status/`),
    answerClarifications: (sessionId, payload) => requestSession(`/api/question-engine/${encodeURIComponent(sessionId)}/answers/`, {
        method: 'POST',
        body: JSON.stringify(payload),
        allowDuplicate: true,
    }),
    cancel: (sessionId) => requestSession(`/api/question-engine/${encodeURIComponent(sessionId)}/cancel/`, {
        method: 'POST',
        allowDuplicate: true,
    }),
    retry: (sessionId) => requestSession(`/api/question-engine/${encodeURIComponent(sessionId)}/retry/`, {
        method: 'POST',
        allowDuplicate: true,
    }),
    approve: (sessionId) => requestSession(`/api/question-engine/${encodeURIComponent(sessionId)}/approve/`, {
        method: 'POST',
        body: JSON.stringify({}),
        allowDuplicate: true,
    }),
    results: (sessionId) => requestSession(`/api/question-engine/${encodeURIComponent(sessionId)}/export.json`),
};

const examPlannerSessionPath = (sessionId, action = '') => (
    `/api/exam-planner/sessions/${encodeURIComponent(sessionId)}/${action}`
);

export const examPlannerApi = {
    sessions: () => request('/api/exam-planner/sessions/', {auth: true}),
    detail: (sessionId) => request(examPlannerSessionPath(sessionId), {auth: true}),
    createSession: ({resourceId, file, subjectType, sourceDescription, plan = {}}) => {
        if (file) {
            const body = new FormData();
            body.append('file', file);
            if (subjectType) body.append('subject_type', subjectType);
            if (sourceDescription) body.append('source_description', sourceDescription);
            body.append('plan', JSON.stringify(plan));
            return request('/api/exam-planner/sessions/', {method: 'POST', auth: true, body});
        }
        return request('/api/exam-planner/sessions/', {
            method: 'POST',
            auth: true,
            body: JSON.stringify({resource_id: resourceId, plan}),
        });
    },
    updateRequirements: (sessionId, payload) => request(examPlannerSessionPath(sessionId, 'requirements/'), {
        method: 'PATCH', auth: true, body: JSON.stringify(payload),
    }),
    agentState: (sessionId) => request(examPlannerSessionPath(sessionId, 'agent-state/'), {auth: true}),
    invokeTool: (sessionId, toolName, args = {}) => request(examPlannerSessionPath(sessionId, 'invoke-tool/'), {
        method: 'POST', auth: true, body: JSON.stringify({tool_name: toolName, arguments: args}),
    }),
    audienceOptions: (sessionId, query = '') => request(
        `${examPlannerSessionPath(sessionId, 'audience-options/')}?q=${encodeURIComponent(query)}`,
        {auth: true},
    ),
    selectAudience: (sessionId, references) => request(examPlannerSessionPath(sessionId, 'select-audience/'), {
        method: 'POST', auth: true, body: JSON.stringify({references}),
    }),
    questionBank: (sessionId) => request(examPlannerSessionPath(sessionId, 'question-bank/'), {auth: true}),
    planQuestions: (sessionId, payload = {}) => request(examPlannerSessionPath(sessionId, 'plan-questions/'), {
        method: 'POST', auth: true, body: JSON.stringify(payload),
    }),
    generateQuestions: (sessionId) => request(examPlannerSessionPath(sessionId, 'generate-questions/'), {
        method: 'POST', auth: true, body: JSON.stringify({}),
    }),
    syncGeneratedQuestions: (sessionId) => request(examPlannerSessionPath(sessionId, 'sync-generated-questions/'), {
        method: 'POST', auth: true, body: JSON.stringify({}), allowDuplicate: true,
    }),
    preview: (sessionId) => request(examPlannerSessionPath(sessionId, 'preview/'), {auth: true}),
    confirm: (sessionId, confirmationToken) => request(examPlannerSessionPath(sessionId, 'confirm/'), {
        method: 'POST', auth: true, body: JSON.stringify({confirmation_token: confirmationToken}),
    }),
};

const teacherAgentDecisionPath = (decisionId, action = '') => (
    `/api/teacher-agent/decisions/${decisionId ? `${encodeURIComponent(decisionId)}/` : ''}${action}`
);

// Teacher Exam Subagent. Keep every state-changing operation on the decision
// resource so the backend can enforce phase and optimistic revision checks.
export const teacherAgentApi = {
    trace: (decisionId) => request(teacherAgentDecisionPath(decisionId, 'trace/'), {auth: true}),
    generateManualDraft: (decisionId) => request(teacherAgentDecisionPath(decisionId, 'generate-text-draft/'), {
        method: 'POST', auth: true, body: JSON.stringify({max_output_tokens: 512}),
    }),
    reviewManualDraft: (decisionId, payload) => request(teacherAgentDecisionPath(decisionId, 'review-text-draft/'), {
        method: 'POST', auth: true, body: JSON.stringify(payload),
    }),
    learnerDrafts: (params = {}) => request(`${teacherAgentDecisionPath(null, 'learner-drafts/')}${buildQueryString(params)}`, {auth: true}),
    learnerDraft: (decisionId) => request(teacherAgentDecisionPath(decisionId, 'learner-draft/'), {auth: true}),
    generateLearnerDraft: (decisionId) => request(teacherAgentDecisionPath(decisionId, 'generate-learner-text-draft/'), {
        method: 'POST', auth: true, body: JSON.stringify({max_output_tokens: 512}),
    }),
    reviewLearnerDraft: (decisionId, payload) => request(teacherAgentDecisionPath(decisionId, 'review-learner-text-draft/'), {
        method: 'POST', auth: true, body: JSON.stringify(payload),
    }),
    publishLearnerResult: (decisionId, hash) => request(teacherAgentDecisionPath(decisionId, 'publish-result/'), {
        method: 'POST', auth: true, body: JSON.stringify({expected_draft_hash: hash}),
    }),
    revokeLearnerResult: (decisionId, hash) => request(teacherAgentDecisionPath(decisionId, 'revoke-result/'), {
        method: 'POST', auth: true, body: JSON.stringify({expected_draft_hash: hash}),
    }),
    createDecision: (payload) => request(teacherAgentDecisionPath(), {
        method: 'POST', auth: true, body: JSON.stringify(payload),
    }),
    detail: (decisionId) => request(teacherAgentDecisionPath(decisionId), {auth: true}),
    revise: (decisionId, payload) => request(teacherAgentDecisionPath(decisionId, 'revise/'), {
        method: 'POST', auth: true, body: JSON.stringify(payload),
    }),
    chooseQuestion: (decisionId, payload) => request(teacherAgentDecisionPath(decisionId, 'choose-question/'), {
        method: 'POST', auth: true, body: JSON.stringify(payload),
    }),
    approveConceptLink: (decisionId, payload) => request(teacherAgentDecisionPath(decisionId, 'approve-concept-link/'), {
        method: 'POST', auth: true, body: JSON.stringify(payload),
    }),
    generate: (decisionId, expectedRevision) => request(teacherAgentDecisionPath(decisionId, 'generate/'), {
        method: 'POST', auth: true, body: JSON.stringify({expected_revision: expectedRevision}),
    }),
    sync: (decisionId) => request(teacherAgentDecisionPath(decisionId, 'sync/'), {
        method: 'POST', auth: true, body: JSON.stringify({}), allowDuplicate: true,
    }),
    retry: (decisionId) => request(teacherAgentDecisionPath(decisionId, 'retry/'), {
        method: 'POST', auth: true, body: JSON.stringify({}),
    }),
    preview: (decisionId) => request(teacherAgentDecisionPath(decisionId, 'preview/'), {
        method: 'POST', auth: true, body: JSON.stringify({}),
    }),
    confirm: (decisionId, confirmationToken) => request(teacherAgentDecisionPath(decisionId, 'confirm/'), {
        method: 'POST', auth: true, body: JSON.stringify({confirmation_token: confirmationToken}),
    }),
    cancel: (decisionId) => request(teacherAgentDecisionPath(decisionId, 'cancel/'), {
        method: 'POST', auth: true, body: JSON.stringify({}),
    }),
};

export const practiceApi = {
    item: (attemptId) => request(`/api/qg/attempts/${encodeURIComponent(attemptId)}/item/`, {auth: true}),
    status: (attemptId) => request(`/api/qg/attempts/${encodeURIComponent(attemptId)}/status/`, {auth: true}),
    submit: (attemptId, payload) => request(`/api/qg/attempts/${encodeURIComponent(attemptId)}/submit/`, {
        method: 'POST', auth: true, allowDuplicate: true, body: JSON.stringify(payload),
    }),
    reviewQueue: () => request('/api/qg/review-queue/', {auth: true}),
    grade: (attemptId, payload) => request(`/api/qg/attempts/${encodeURIComponent(attemptId)}/grade/`, {
        method: 'POST', auth: true, body: JSON.stringify(payload),
    }),
};

const examPath = (scope, suffix = '') => `/api/exams/${scope}/exams/${suffix}`;

export const examsApi = {
    master: {
        list: (params = {}) => request(`${examPath('master')}${buildQueryString(params)}`, {auth: true}),
        detail: (id) => request(examPath('master', `${id}/`), {auth: true}),
        create: (payload) => request(examPath('master'), {method: 'POST', auth: true, body: JSON.stringify(payload)}),
        update: (id, payload) => request(examPath('master', `${id}/`), {method: 'PATCH', auth: true, body: JSON.stringify(payload)}),
        remove: (id) => request(examPath('master', `${id}/`), {method: 'DELETE', auth: true}),
        setQuestions: (id, questionIds) => request(examPath('master', `${id}/set-questions/`), {
            method: 'POST',
            auth: true,
            body: JSON.stringify({question_ids: questionIds}),
        }),
        removeQuestion: (id, examQuestionId) => request(examPath('master', `${id}/questions/${examQuestionId}/`), {
            method: 'DELETE',
            auth: true,
        }),
        publishCheck: (id) => request(examPath('master', `${id}/publish-check/`), {auth: true}),
        preview: (id) => request(examPath('master', `${id}/preview/`), {auth: true}),
        attempts: (id, params = {}) => request(
            `${examPath('master', `${id}/attempts/`)}${buildQueryString(params)}`,
            {auth: true},
        ),
        attemptDetail: (id, attemptId) => request(
            examPath('master', `${id}/attempts/${attemptId}/`),
            {auth: true},
        ),
        aaeGradeAttempt: (id, attemptId) => request(examPath('master', `${id}/attempts/${attemptId}/aae-grade/`), {
            method: 'POST',
            auth: true,
            body: JSON.stringify({}),
        }),
        resetAttempt: (id, attemptId) => request(
            examPath('master', `${id}/attempts/${attemptId}/reset/`),
            {method: 'POST', auth: true},
        ),
        link: (id) => request(examPath('master', `${id}/link/`), {auth: true}),
        publish: (id) => request(examPath('master', `${id}/publish/`), {method: 'POST', auth: true}),
        unpublish: (id) => request(examPath('master', `${id}/unpublish/`), {method: 'POST', auth: true}),
        close: (id) => request(examPath('master', `${id}/close/`), {method: 'POST', auth: true}),
        releaseResults: (id) => request(examPath('master', `${id}/release-results/`), {method: 'POST', auth: true}),
        purgeQuestionDetails: (id) => request(examPath('master', `${id}/purge-question-details/`), {method: 'POST', auth: true}),
        gradeAnswer: (id, answerId, payload) => request(examPath('master', `${id}/answers/${answerId}/grade/`), {
            method: 'POST',
            auth: true,
            body: JSON.stringify(payload),
        }),
        aaeReviewAnswer: (id, answerId) => request(examPath('master', `${id}/answers/${answerId}/aae-review/`), {
            method: 'POST',
            auth: true,
            body: JSON.stringify({}),
            allowDuplicate: true,
        }),
        gradeAttempt: (id, attemptId, grades) => request(examPath('master', `${id}/attempts/${attemptId}/grades/`), {
            method: 'POST',
            auth: true,
            body: JSON.stringify({grades}),
        }),
        accommodation: (id, payload) => request(examPath('master', `${id}/accommodation/`), {
            method: 'POST',
            auth: true,
            body: JSON.stringify(payload),
        }),
        analytics: (id, params = {}) => request(
            `${examPath('master', `${id}/analytics/`)}${buildQueryString(params)}`,
            {auth: true},
        ),
        results: (id, params = {}) => request(
            `${examPath('master', `${id}/results/`)}${buildQueryString(params)}`,
            {auth: true},
        ),
        events: (id, params = {}) => request(
            `${examPath('master', `${id}/events/`)}${buildQueryString(params)}`,
            {auth: true},
        ),
        reviewEvent: (id, eventId) => request(
            examPath('master', `${id}/events/${eventId}/review/`),
            {method: 'POST', auth: true},
        ),
        reviewEvents: (id, eventIds) => request(examPath('master', `${id}/events/review/`), {
            method: 'POST',
            auth: true,
            body: JSON.stringify({event_ids: eventIds}),
        }),
        exportResults: (id, format, params = {}) => requestBlob(
            `${examPath('master', `${id}/export-results/${encodeURIComponent(format)}/`)}${buildQueryString(params)}`,
            {auth: true},
        ),
        retryAae: (id, answerIds) => request(examPath('master', `${id}/aae/retry/`), {
            method: 'POST',
            auth: true,
            body: JSON.stringify({answer_ids: answerIds}),
        }),
        gradeRevisions: (id, answerId) => request(
            examPath('master', `${id}/answers/${answerId}/grade-revisions/`),
            {auth: true},
        ),
    },
    student: {
        list: () => request(examPath('student'), {auth: true}),
        results: () => request(examPath('student', 'results/'), {auth: true}),
        detail: (id) => request(examPath('student', `${id}/`), {auth: true}),
        join: (joinCode) => request(examPath('student', `join/${encodeURIComponent(joinCode)}/`), {method: 'POST', auth: true}),
        start: (id) => request(examPath('student', `${id}/start/`), {method: 'POST', auth: true}),
        saveAnswers: (id, answers) => request(examPath('student', `${id}/save-answers/`), {
            method: 'POST',
            auth: true,
            body: JSON.stringify({answers}),
        }),
        submit: (id, answers) => request(examPath('student', `${id}/submit/`), {
            method: 'POST',
            auth: true,
            allowDuplicate: true,
            body: JSON.stringify({answers}),
        }),
        recordEvent: (id, eventType, metadata = {}) => request(examPath('student', `${id}/events/`), {
            method: 'POST',
            auth: true,
            allowDuplicate: true,
            body: JSON.stringify({event_type: eventType, metadata}),
        }),
        notifications: () => request(examPath('student', 'notifications/'), {auth: true}),
        readNotification: (notificationId) => request(
            examPath('student', `notifications/${notificationId}/read/`),
            {method: 'POST', auth: true},
        ),
    },
};

const examFeedbackPath = (suffix = '') => `/api/exam-feedback/${suffix}`;

export const examFeedbackApi = {
    student: {
        eligibility: ({attemptId, answerId}) => request(
            `${examFeedbackPath('student/eligibility/')}${buildQueryString({
                attempt_id: attemptId,
                ...(answerId === null || answerId === undefined ? {} : {answer_id: answerId}),
            })}`,
            {auth: true},
        ),
        feedback: (params = {}) => request(
            `${examFeedbackPath('student/feedback/')}${buildQueryString(params)}`,
            {auth: true},
        ),
        createFeedback: (payload, idempotencyKey) => request(examFeedbackPath('student/feedback/'), {
            method: 'POST',
            auth: true,
            headers: {'Idempotency-Key': idempotencyKey},
            body: JSON.stringify(payload),
        }),
        updateFeedback: (id, payload) => request(examFeedbackPath(`student/feedback/${id}/`), {
            method: 'PATCH',
            auth: true,
            body: JSON.stringify(payload),
        }),
        appeals: (params = {}) => request(
            `${examFeedbackPath('student/appeals/')}${buildQueryString(params)}`,
            {auth: true},
        ),
        createAppeal: (payload, idempotencyKey) => request(examFeedbackPath('student/appeals/'), {
            method: 'POST',
            auth: true,
            headers: {'Idempotency-Key': idempotencyKey},
            body: JSON.stringify(payload),
        }),
        appealDetail: (id) => request(examFeedbackPath(`student/appeals/${id}/`), {auth: true}),
        withdrawAppeal: (id, revision) => request(examFeedbackPath(`student/appeals/${id}/withdraw/`), {
            method: 'POST',
            auth: true,
            body: JSON.stringify({revision}),
        }),
    },
    master: {
        appeals: (params = {}) => request(
            `${examFeedbackPath('master/appeals/')}${buildQueryString(params)}`,
            {auth: true},
        ),
        appealDetail: (id) => request(examFeedbackPath(`master/appeals/${id}/`), {auth: true}),
        startReview: (id, revision) => request(examFeedbackPath(`master/appeals/${id}/start-review/`), {
            method: 'POST',
            auth: true,
            body: JSON.stringify({revision}),
        }),
        resolveAppeal: (id, payload) => request(examFeedbackPath(`master/appeals/${id}/resolve/`), {
            method: 'POST',
            auth: true,
            body: JSON.stringify(payload),
        }),
        feedback: (params = {}) => request(
            `${examFeedbackPath('master/feedback/')}${buildQueryString(params)}`,
            {auth: true},
        ),
        analytics: (params = {}) => request(
            `${examFeedbackPath('master/feedback/analytics/')}${buildQueryString(params)}`,
            {auth: true},
        ),
        policy: (examId) => request(examFeedbackPath(`master/exams/${examId}/policy/`), {auth: true}),
        updatePolicy: (examId, payload) => request(examFeedbackPath(`master/exams/${examId}/policy/`), {
            method: 'PATCH',
            auth: true,
            body: JSON.stringify(payload),
        }),
    },
};
